import { Badge, Box, Flex, Heading, HStack, Spinner, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PortalAwardModel, type PortalSourcingEventDetailDto } from "../../../../../autogen/bff-api";
import { PriceWeightBreakdown } from "../../../../../common/award-criteria/PriceWeightBreakdown";
import { answerRowsOf, priceWeightRowsOf } from "../../../../../common/award-criteria/scorableAwardRows";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { AwardCriterionAnswerCard } from "../AwardCriterionAnswerCard";
import { useAwardCriterionAnswers } from "../useAwardCriterionAnswers";

interface Props {
  bidId: string;
  event: PortalSourcingEventDetailDto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const AwardCriteriaStep = ({ bidId, event, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const {
    groups,
    rows,
    answerRows,
    responseFor,
    upsert,
    price,
    priceComesFromProducts,
    priceEditingLocked,
    currency,
    setPrice,
    answeredCount,
    isSaving,
    readOnly,
  } = useAwardCriterionAnswers(bidId, event);
  const isLowestPrice = event.awardCriteria?.model === PortalAwardModel.LowestPrice;

  return (
    <Box pb="8" pt="6">
      <Flex align={{ base: "start", md: "center" }} justify="space-between" gap="3" mb="2" flexWrap="wrap">
        <HStack spacing="2" align="center" flexWrap="wrap">
          <Heading size="md">{t("Award criteria")}</Heading>
          <Badge bg="smPrimarySofter" color="smPrimary" px="2" py="0.5" borderRadius="md" textTransform="none">
            {t("Buyer criteria")}
          </Badge>
          {isSaving && <Spinner size="sm" color="smMuted" />}
        </HStack>
        {rows.length > 0 && (
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color={answeredCount === answerRows.length ? "smSuccess" : "smMuted"}
          >
            {t("{{answered}} of {{total}} answered", { answered: answeredCount, total: answerRows.length })}
          </Text>
        )}
      </Flex>
      <Stack spacing="1" mb="6">
        <Text fontSize="sm" color="smMuted">
          {isLowestPrice
            ? t("This procurement is awarded on the lowest price. State your total price below.")
            : t(
                "Answer the buyer's award criteria. They are grouped by main criterion and weighted in the evaluation of your offer.",
              )}
        </Text>
        {/* Only worth saying where a criterion actually demands a file. */}
        {rows.some((row) => row.requiresDocumentation) && (
          <Text fontSize="sm" color="smMuted">
            {t(
              'Where a criterion is marked "Documentation required", an attachment is needed for the answer to count as complete.',
            )}
          </Text>
        )}
      </Stack>

      {rows.length === 0 && (
        <Box borderWidth="1px" borderColor="smBorder" rounded="lg" bg="smBackground" px="4" py="3" mb="6">
          <Text fontSize="sm" color="smMuted">
            {t("This procurement has no award criteria to answer.")}
          </Text>
        </Box>
      )}

      <Stack spacing="6" mb="8">
        {groups.map((group) => (
          <Stack key={group.key} spacing="3">
            {group.hasOwnHeading && (
              <HStack spacing="3" align="baseline" flexWrap="wrap">
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" color="smText">
                  {group.label}
                </Text>
                {group.weightPercent != null && (
                  <Text fontSize="sm" color="smMuted">
                    {t("Weight in evaluation")}: <b>{group.weightPercent} %</b>
                  </Text>
                )}
              </HStack>
            )}
            {answerRowsOf(group).map((row) => (
              <AwardCriterionAnswerCard
                key={row.id}
                bidId={bidId}
                row={row}
                response={responseFor(row)}
                price={price}
                priceComesFromProducts={priceComesFromProducts}
                priceEditingLocked={priceEditingLocked}
                currency={currency}
                isReadOnly={readOnly}
                onChange={(patch) => upsert(row, patch)}
                onPriceChange={setPrice}
              />
            ))}
            <PriceWeightBreakdown rows={priceWeightRowsOf(group)} />
          </Stack>
        ))}
      </Stack>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
